import { useState, type ReactNode } from 'react';

/**
 * O CHASSI de toda busca de ativo — 08/09/2026.
 *
 * > *"Precisamos corrigir de forma profissional sobre a padronização do Hub
 * > Kairós e os botões."* — Richard
 *
 * ⛔ **A aba tinha TRÊS formatos de busca ao mesmo tempo**, e ele se perdeu
 * neles: veículos era um lote no topo com caixas de marcar, imóveis tinha
 * **dois** botões fazendo quase a mesma coisa (um no topo e um por
 * investigado), e aeronaves e processos ficavam só dentro do investigado.
 * Cada um dizia o custo de um jeito, ou não dizia.
 *
 * ⭐ **Agora toda busca segue a mesma ordem, e é a ordem da decisão:**
 *
 * 1. **o que esta busca alcança** — dito antes, porque resultado vazio numa
 *    base estreita é lido como ausência de patrimônio;
 * 2. **o que exatamente será consultado** — foi a falta disto que fez quatro
 *    vazios corretos parecerem defeito em 08/09;
 * 3. **quanto custa**, antes do clique;
 * 4. o resultado, no mesmo lugar.
 *
 * ⚠️ **Consulta paga sempre pede confirmação; gratuita nunca pede.** Um passo a
 * mais numa busca que não custa nada ensina quem usa a clicar sem ler — e aí a
 * confirmação da que custa também passa batida.
 */
export type CustoDaBusca =
  | { tipo: 'gratuita' }
  | {
      tipo: 'paga';
      /** Quantas consultas este clique dispara. */
      quantas: number;
      /**
       * O preço de tabela por consulta, **vindo do servidor**.
       * ⚠️ `null` = não tabelado, e a tela **diz isso** em vez de calar: botão
       * que parece gratuito é o que faz a fatura chegar com surpresa.
       */
      unitario: number | null;
      /**
       * ⭐ **O preço foi MEDIDO no histórico, não lido de uma tabela — 22/09/2026.**
       *
       * ⚠️ **A diferença importa na tela.** *"a R$ 0,18 cada"* é uma promessa
       * exata; quando o número sai da média de consultas já feitas, a próxima
       * pode custar outra coisa — a Infosimples cobra por faixa de volume, e as
       * 39 do SPU saíram entre R$ 0,16 e R$ 0,18. **Prometer exatidão que não
       * se tem é pior do que dizer "cerca de".**
       *
       * ⛔ **Não é jeito de fugir da tabela:** quando o preço de contrato
       * entrar, esta marca sai junto.
       */
      aproximado?: true;
    };

export function BuscaDeAtivo({
  rotulo,
  alcance,
  oQueSeraOlhado,
  custo,
  avisos,
  buscando,
  erro,
  rotuloBuscando,
  aoBuscar,
  unidade = ['documento', 'documentos'],
  children,
}: {
  /** O verbo do botão: "Buscar imóveis". O custo entra sozinho, ao lado. */
  rotulo: string;
  /** O que a fonte cobre — e, quando é recorte, o que ela **não** cobre. */
  alcance: ReactNode;
  /** A lista dos documentos que serão consultados. Some quando não há o que dizer. */
  oQueSeraOlhado?: ReactNode;
  custo: CustoDaBusca;
  /** Avisos que só aparecem na hora de confirmar o gasto. */
  avisos?: ReactNode;
  buscando: boolean;
  erro: string | null;
  rotuloBuscando?: string;
  aoBuscar: () => void;
  /**
   * ⭐ O que cada consulta leva — "documento" por padrão. A busca no exterior
   * (28/09/2026) vai pelo NOME, e "vou consultar 1 documento" seria falso.
   */
  unidade?: [string, string];
  /** O resultado, desenhado por quem chama. */
  children?: ReactNode;
}) {
  const [confirmar, setConfirmar] = useState(false);

  const gratuita = custo.tipo === 'gratuita';
  const quantas = custo.tipo === 'paga' ? custo.quantas : 0;
  const total =
    custo.tipo === 'paga' && custo.unitario !== null ? custo.unitario * custo.quantas : null;

  /*
   * ⚠️ **O rótulo diz o tamanho do gasto, não só o preço.** "Buscar imóveis —
   * 3 documentos" responde à pergunta que decide o clique; só o preço unitário
   * esconderia que ele será cobrado três vezes.
   */
  const noBotao = gratuita
    ? `${rotulo} — grátis`
    : total !== null
      ? `${rotulo} — ${quantas} ${quantas === 1 ? unidade[0] : unidade[1]} · ${
          custo.tipo === 'paga' && custo.aproximado ? '≈ ' : ''
        }R$ ${total.toFixed(2).replace('.', ',')}`
      : `${rotulo} — ${quantas} ${quantas === 1 ? unidade[0] : unidade[1]}`;

  return (
    <div className="busca-de-ativo">
      {!confirmar ? (
        <button
          type="button"
          className="botao-secundario"
          disabled={buscando || (custo.tipo === 'paga' && quantas === 0)}
          onClick={() => (gratuita ? aoBuscar() : setConfirmar(true))}
        >
          {buscando ? (rotuloBuscando ?? 'Buscando…') : noBotao}
        </button>
      ) : (
        <div className="aviso-de-custo">
          <p>
            Vou consultar{' '}
            <strong>
              {quantas} {quantas === 1 ? unidade[0] : unidade[1]}
            </strong>
            {total !== null ? (
              <>
                , a{custo.tipo === 'paga' && custo.aproximado ? ' cerca de' : ''} R${' '}
                {(custo.tipo === 'paga' ? custo.unitario! : 0).toFixed(2).replace('.', ',')} cada —{' '}
                <strong>
                  {custo.tipo === 'paga' && custo.aproximado ? '≈ ' : ''}R${' '}
                  {total.toFixed(2).replace('.', ',')}
                </strong>{' '}
                no total.
              </>
            ) : (
              <>
                . ⚠️ <strong>O preço ainda não está tabelado no Hub</strong>, e a fonte informa o
                custo real de cada resposta no histórico de consultas.
              </>
            )}
          </p>
          {avisos}
          <button
            type="button"
            className="botao-primario"
            disabled={buscando}
            onClick={() => {
              setConfirmar(false);
              aoBuscar();
            }}
          >
            {buscando ? (rotuloBuscando ?? 'Buscando…') : 'Consultar'}
          </button>
          <button type="button" className="botao-texto" onClick={() => setConfirmar(false)}>
            Cancelar
          </button>
        </div>
      )}

      {/*
        ⚠️ **O alcance vem SEMPRE, e não só quando dá vazio.** Base estreita que
        não se anuncia faz o vazio parecer ausência de patrimônio — e foi
        exatamente isso que aconteceu em 08/09, quando quatro respostas corretas
        pareceram defeito.
      */}
      <p className="ajuda">{alcance}</p>

      {oQueSeraOlhado && (
        <details className="o-que-sera-olhado">
          <summary>O que será consultado</summary>
          {oQueSeraOlhado}
        </details>
      )}

      {/*
        ⚠️ **Erro que a tela não mostra é erro que não existe** — a lição de
        21/08, quando a Assertiva recusou por horário e a tela ficou muda.
      */}
      {erro && <p className="erro">{erro}</p>}

      {children}
    </div>
  );
}
